import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import Lightbox from '../components/Lightbox';
import OrderForm from '../components/OrderForm';
import { imageUrl } from '../lib/images';
import {
  fetchCollection,
  FALLBACK_YDELSER,
  FALLBACK_REFERENCER,
  FALLBACK_GALLERI,
  getSlug
} from '../lib/cms';

export default function YdelseDetail() {
  const { slug } = useParams();
  const [ydelse, setYdelse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState(null);
  const [relatedRefs, setRelatedRefs] = useState([]);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxCaptions, setLightboxCaptions] = useState([]);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const [ydData, galData, refData] = await Promise.all([
        fetchCollection('ydelser', FALLBACK_YDELSER),
        fetchCollection('galleri', FALLBACK_GALLERI),
        fetchCollection('referencer', FALLBACK_REFERENCER)
      ]);

      const foundYdelse = (ydData || FALLBACK_YDELSER).find(
        (item) => getSlug(item) === slug || item.slug === slug || (typeof item.slug === 'object' && item.slug.da === slug)
      );

      setYdelse(foundYdelse || null);

      if (foundYdelse) {
        // Resolve CMS Gallery relation via galleri_slug
        if (foundYdelse.galleri_slug && galData) {
          const matchGal = galData.find(
            (g) => g.slug === foundYdelse.galleri_slug || (typeof g.slug === 'object' && g.slug.da === foundYdelse.galleri_slug)
          );
          setGallery(matchGal || null);
        }

        // Resolve CMS Reference relations via ydelse_slug
        if (refData) {
          const matchRefs = refData.filter(
            (r) => r.ydelse_slug === slug
          );
          setRelatedRefs(matchRefs);
        }
      }

      setLoading(false);
    }
    loadData();
  }, [slug]);

  const openLightbox = (imgs, captions = [], index = 0) => {
    setLightboxImages(imgs);
    setLightboxCaptions(captions);
    setCurrentImgIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="flex items-center gap-3">
          <i className="fa-solid fa-circle-notch animate-spin text-sky-400 text-2xl" aria-hidden="true" />
          <span className="text-slate-300">Indlæser ydelse...</span>
        </div>
      </div>
    );
  }

  if (!ydelse) {
    return (
      <div className="min-h-screen bg-slate-50 py-24 px-4 text-center">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Ydelsen blev ikke fundet</h1>
        <p className="text-slate-600 mb-8">Vi kunne desværre ikke finde den ønskede underside.</p>
        <Link
          to="/ydelser"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-900 text-white font-semibold text-sm"
        >
          <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
          <span>Tilbage til oversigten</span>
        </Link>
      </div>
    );
  }

  const primaryImg = ydelse.billede?.[0];
  const galleryImages = gallery?.billeder || ydelse.billede || [];
  const galleryCaptions = gallery?.alt_tekster ? gallery.alt_tekster.split('||').map((s) => s.trim()) : [];

  return (
    <div className="bg-slate-50 min-h-screen">
      <PageMeta
        title={`${ydelse.titel} - Duus Rustfri`}
        description={ydelse.kort_beskrivelse}
      />

      {/* Header Banner */}
      <section className="bg-slate-950 text-white py-16 sm:py-24 border-b border-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={imageUrl(primaryImg, 'width=1600,quality=70,format=auto')}
            alt=""
            className="w-full h-full object-cover blur-sm"
          />
          <div className="absolute inset-0 bg-slate-950/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-400 hover:text-sky-300 transition"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>Tilbage til alle ydelser</span>
            </Link>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-4">
              {ydelse.titel}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              {ydelse.kort_beskrivelse}
            </p>

            <div className="pt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <a
                href="#bestil"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-500 hover:to-blue-600 text-white font-semibold text-sm shadow-md transition text-center"
              >
                <span>Få tilbud på denne ydelse</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </a>

              <a
                href="tel:30271671"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 border border-slate-700 hover:bg-slate-800 text-white font-semibold text-sm transition whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone text-sky-400" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring 30 27 16 71</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Detailed Description & Technical Specifications */}
            <div className="lg:col-span-7 space-y-8">
              <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-sm space-y-6">
                <h2 className="text-2xl font-bold text-slate-950">
                  Om {ydelse.titel.toLowerCase()}
                </h2>

                <div
                  className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: ydelse.beskrivelse }}
                />

                <div className="pt-6 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {ydelse.materialer && (
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <span className="block text-xs uppercase font-bold text-slate-500 mb-1">
                        Materialer
                      </span>
                      <p className="text-sm font-semibold text-slate-900">
                        {ydelse.materialer}
                      </p>
                    </div>
                  )}

                  {ydelse.fordele && (
                    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                      <span className="block text-xs uppercase font-bold text-slate-500 mb-1">
                        Karakteristika
                      </span>
                      <p className="text-sm font-semibold text-slate-900">
                        {ydelse.fordele}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Related References from CMS if available */}
              {relatedRefs.length > 0 && (
                <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-sm space-y-6">
                  <h3 className="text-xl font-bold text-slate-950 flex items-center gap-2">
                    <i className="fa-solid fa-diagram-project text-sky-600" aria-hidden="true" />
                    <span>Relevante referenceprojekter</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedRefs.map((ref) => (
                      <Link
                        key={ref.id || getSlug(ref)}
                        to={`/referencer/${getSlug(ref)}`}
                        className="group p-4 rounded-xl border border-slate-200 hover:border-sky-400 bg-slate-50 hover:bg-white transition flex flex-col justify-between"
                      >
                        <div>
                          <p className="text-xs font-mono uppercase text-sky-700">{ref.opgavetype}</p>
                          <p className="font-bold text-slate-900 text-sm mt-1 group-hover:text-sky-700 transition">
                            {ref.titel}
                          </p>
                        </div>
                        <span className="text-xs text-sky-600 font-semibold mt-3 inline-flex items-center gap-1">
                          <span>Se projekt</span>
                          <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: CMS Gallery Photos & Quick Contact Card */}
            <div className="lg:col-span-5 space-y-8">
              {/* CMS Gallery Connected by slug */}
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-950">
                    {gallery?.titel || 'Billeder af opgaver'}
                  </h3>
                  <span className="text-xs text-slate-500 font-mono">
                    {galleryImages.length} fotos
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {galleryImages.map((img, idx) => (
                    <div
                      key={idx}
                      className="relative aspect-square rounded-xl overflow-hidden bg-slate-900 cursor-pointer group shadow-sm"
                      onClick={() => openLightbox(galleryImages, galleryCaptions, idx)}
                    >
                      <img
                        src={imageUrl(img, 'width=400,height=400,fit=cover,quality=80,format=auto')}
                        alt={galleryCaptions[idx] || ydelse.titel}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                        <i className="fa-solid fa-magnifying-glass-plus text-sm" aria-hidden="true" />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Klik for at se billeder i fuld opløsning
                </p>
              </div>

              {/* Direct Workshop Contact Box */}
              <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 border border-slate-800 shadow-xl space-y-4">
                <span className="text-xs uppercase font-mono tracking-wider text-sky-400">
                  Direkte kontakt
                </span>
                <h3 className="text-xl font-bold">Har du en opgave i tankerne?</h3>
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Jeg vurderer gerne din idé eller tegning og giver en ærlig tilbagemelding og et uforpligtende prisoverslag.
                </p>

                <div className="pt-2 space-y-2 text-sm">
                  <a
                    href="tel:30271671"
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-800 text-sky-400 font-semibold transition whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span className="whitespace-nowrap">Tlf: 30 27 16 71</span>
                  </a>

                  <a
                    href="mailto:Duus.rustfri@gmail.com"
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-200 hover:text-white transition whitespace-nowrap text-xs sm:text-sm"
                  >
                    <i className="fa-solid fa-envelope text-sky-400" aria-hidden="true" />
                    <span className="whitespace-nowrap">Duus.rustfri@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form Pre-Configured for this service */}
      <section id="bestil" className="py-16 sm:py-24 bg-slate-950 text-white scroll-mt-20 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <OrderForm
            defaultOpgavetype={ydelse.titel}
            title={`Forespørgsel: ${ydelse.titel}`}
            subtitle="Udfyld formularen for at modtage et uforpligtende tilbud på denne type opgave."
          />
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        captions={lightboxCaptions}
        currentIndex={currentImgIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setCurrentImgIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1))}
        onNext={() => setCurrentImgIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0))}
        onSelectIndex={(idx) => setCurrentImgIndex(idx)}
      />
    </div>
  );
}
